import { useState, ReactNode } from 'react';
import styled from 'styled-components';
import { Complaint, ComplaintLevel } from '@ardoq/api-types/integrations';
import { AnimatedChevron } from '@ardoq/icons';
import _ from 'lodash/fp';
import { Stack, FlexBox } from '@ardoq/layout';
import { s8, s16 } from '@ardoq/design-tokens';
import { text2 } from '@ardoq/typography';
import { ButtonSize, GhostButton } from '@ardoq/button';
import { ErrorNotification, WarningNotification } from '@ardoq/status-ui';
import { type I18nLocale, t } from '@ardoq/i18n';

type ComplaintsProps = {
  errors?: Complaint[];
  warnings?: Complaint[];
  collapsiblePanels: boolean;
  collapseThreshold?: number;
  i18nLocale: I18nLocale;
};

export const Complaints = ({
  errors = [],
  warnings = [],
  collapsiblePanels,
  collapseThreshold = 5,
  i18nLocale,
}: ComplaintsProps) =>
  errors.length || warnings.length ? (
    <ComplaintsContainer>
      <ErrorPanel
        isCollapsible={collapsiblePanels}
        errors={errors}
        collapseThreshold={collapseThreshold}
        i18nLocale={i18nLocale}
      />
      <WarningPanel
        isCollapsible={collapsiblePanels}
        warnings={warnings}
        collapseThreshold={collapseThreshold}
        i18nLocale={i18nLocale}
      />
    </ComplaintsContainer>
  ) : null;

export const ErrorPanel = ({
  errors,
  isCollapsible,
  collapseThreshold,
  i18nLocale,
}: {
  errors: Complaint[];
  isCollapsible?: boolean;
  collapseThreshold?: number;
  i18nLocale: I18nLocale;
}) => {
  if (errors.length > 0) {
    return (
      <ComplaintPanel
        isCollapsible={isCollapsible ?? false}
        level="error"
        complaints={renderComplaints(errors)}
        collapseThreshold={collapseThreshold}
        i18nLocale={i18nLocale}
      />
    );
  }
  return <></>;
};

export const WarningPanel = ({
  warnings,
  isCollapsible,
  collapseThreshold,
  i18nLocale,
}: {
  warnings: Complaint[];
  isCollapsible?: boolean;
  collapseThreshold?: number;
  i18nLocale: I18nLocale;
}) => {
  if (warnings.length > 0) {
    return (
      <ComplaintPanel
        isCollapsible={isCollapsible ?? false}
        level="warn"
        complaints={renderComplaints(warnings)}
        collapseThreshold={collapseThreshold}
        i18nLocale={i18nLocale}
      />
    );
  }
  return <></>;
};

type ComplaintsPanelProps = {
  level: ComplaintLevel;
  complaints: ReactNode[];
  isCollapsible: boolean;
  collapseThreshold?: number;
  i18nLocale: I18nLocale;
};

const getComplaintTotal = (
  i18nLocale: I18nLocale,
  level: ComplaintLevel,
  count: number
): string => {
  if (level === 'error') {
    return count === 1
      ? t(
          i18nLocale,
          'integrations:complaints.errorCount.one',
          { count },
          '{{count}} error'
        )
      : t(
          i18nLocale,
          'integrations:complaints.errorCount.other',
          { count },
          '{{count}} errors'
        );
  }
  return count === 1
    ? t(
        i18nLocale,
        'integrations:complaints.warningCount.one',
        { count },
        '{{count}} warning'
      )
    : t(
        i18nLocale,
        'integrations:complaints.warningCount.other',
        { count },
        '{{count}} warnings'
      );
};

const ComplaintPanel = ({
  level,
  complaints,
  isCollapsible,
  collapseThreshold = 5,
  i18nLocale,
}: ComplaintsPanelProps) => {
  const [isCollapsed, setCollapsed] = useState(isCollapsible);
  const visibleComplaints = isCollapsed
    ? _.take(collapseThreshold, complaints)
    : complaints;

  const showMoreButtonDisplayed =
    isCollapsible && collapseThreshold < complaints.length;

  const content = (
    <ContentContainer>
      <ComplaintTotal>
        {getComplaintTotal(i18nLocale, level, complaints.length)}
      </ComplaintTotal>
      <Stack gap="xsmall">{visibleComplaints}</Stack>
      {showMoreButtonDisplayed ? (
        <FlexBox justify={'end'} gap="medium">
          <GhostButton
            buttonSize={ButtonSize.SMALL}
            onClick={() => {
              setCollapsed(!isCollapsed);
            }}
          >
            {isCollapsed
              ? t(i18nLocale, 'integrations:complaints.showMore', 'Show more')
              : t(i18nLocale, 'integrations:complaints.showLess', 'Show less')}
            <AnimatedChevron $isUp={!isCollapsed} />
          </GhostButton>
        </FlexBox>
      ) : (
        <></>
      )}
    </ContentContainer>
  );

  if (level === 'error') {
    return <ErrorNotification>{content}</ErrorNotification>;
  }
  if (level === 'warn') {
    return <WarningNotification>{content}</WarningNotification>;
  }
  return <></>;
};

// compute the precision level of a complaint
// 4 : cell level
// 3 : row level
// 2 : column level
// 1 : sheet level
const complaintPrecisionLevel = ({ target }: Complaint): number => {
  return target === 'cell'
    ? 4
    : target === 'row'
      ? 3
      : target === 'column'
        ? 2
        : 1;
};

const renderComplaints = _.flow(
  _.groupBy((e: Complaint) => e.message),
  _.toPairs,
  _.sortBy(([_message, complaints]) =>
    _.min(_.map(complaintPrecisionLevel, complaints))
  ),
  _.entries,
  _.map(([index, [message, complaints]]) => (
    <ComplaintMessage key={`${index}`}>
      <ComplaintMessageCount>{complaints.length}x</ComplaintMessageCount>
      <ComplaintMessageText>{message}</ComplaintMessageText>
    </ComplaintMessage>
  ))
);

const ComplaintTotal = styled.div`
  margin-bottom: ${s8};
  ${text2}
`;

const ComplaintMessage = styled(FlexBox).attrs({ gap: 'xsmall' })`
  ${text2}
`;

const ComplaintMessageCount = styled.div`
  flex-shrink: 0;
`;

const ComplaintMessageText = styled.div`
  flex-shrink: 1;
`;

const ContentContainer = styled(FlexBox).attrs({
  gap: 'xsmall',
  flexDirection: 'column',
})`
  padding: 0;
  max-height: calc(256px - ${s16});
  overflow: auto;
`;

const ComplaintsContainer = styled(FlexBox).attrs({ gap: 'xlarge' })`
  > * {
    flex: 1;
    align-self: flex-start;
    padding: ${s16};
    max-height: 256px;
  }
`;
